import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { formatShortLocation } from '@/utils/location';
import {
  ArrowLeft,
  Bookmark,
  BookmarkCheck,
  Car,
  ChevronDown,
  ChevronUp,
  Clock,
  MapPin,
  Plus,
  Route,
  Share2,
  Trash2,
  X,
} from 'lucide-react';

const formatDuration = (minutes) => {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

// Inner body content — reused by both the desktop persistent sidebar and the
// mobile bottom sheet. Keep this component pure (no outer wrapper) so both
// call sites control positioning independently.
export const RouteSidebarBody = ({
  user,
  currentRoute,
  selectedStops,
  routeInfo,
  searchQuery,
  setSearchQuery,
  filteredEntities,
  savedRoutes,
  navigate,
  openSaveDialog,
  setSavedDrawerOpen,
  clearRoute,
  moveStop,
  removeStop,
  addStop,
  openShareDialog,
  hideBackButton = false,
}) => {
  return (
    <>
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Route className="h-5 w-5 text-primary" />
            <h2 className="font-semibold">Route Planner</h2>
            {currentRoute && (
              <Badge variant="outline" className="text-xs" data-testid="current-route-badge">
                <BookmarkCheck className="h-3 w-3 mr-1 text-primary" />
                {currentRoute.name}
              </Badge>
            )}
          </div>
          {!hideBackButton && (
            <Button variant="ghost" size="sm" onClick={() => navigate('/discovery')}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          )}
        </div>

        {/* Save / Saved actions */}
        {user && (
          <div className="flex items-center gap-2 mb-3">
            <Button
              variant="outline"
              size="sm"
              onClick={openSaveDialog}
              className="flex-1"
              data-testid="save-route-btn"
            >
              <Bookmark className="h-3.5 w-3.5 mr-1" />
              {currentRoute ? 'Update' : 'Save'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSavedDrawerOpen(true)}
              data-testid="saved-routes-btn"
            >
              <BookmarkCheck className="h-3.5 w-3.5 mr-1" />
              Saved {savedRoutes.length > 0 && `(${savedRoutes.length})`}
            </Button>
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search destinations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="route-search-input"
          />
        </div>
      </div>

      {/* Selected Stops */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium">Your Route ({selectedStops.length} stops)</h3>
          {selectedStops.length > 0 && (
            <Button variant="ghost" size="sm" onClick={clearRoute} className="text-destructive h-7">
              <Trash2 className="h-3 w-3 mr-1" />
              Clear
            </Button>
          )}
        </div>

        {selectedStops.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            Click on destinations below or markers on the map to add stops
          </p>
        ) : (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {selectedStops.map((stop, index) => (
              <div
                key={stop.entity_id}
                className="flex items-center gap-2 p-2 rounded-lg bg-muted/50 group"
              >
                <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold shrink-0">
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{stop.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{stop.location}</p>
                </div>
                <div className="flex items-center gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 md:h-6 md:w-6"
                    onClick={() => moveStop(index, -1)}
                    disabled={index === 0}
                  >
                    <ChevronUp className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 md:h-6 md:w-6"
                    onClick={() => moveStop(index, 1)}
                    disabled={index === selectedStops.length - 1}
                  >
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 md:h-6 md:w-6 text-destructive"
                    onClick={() => removeStop(stop.entity_id)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Route Info */}
        {routeInfo && (
          <div className="mt-4 p-3 rounded-xl bg-primary/10 border border-primary/20">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Route Summary</span>
              <Button variant="ghost" size="sm" onClick={openShareDialog} className="h-7" data-testid="share-route-btn">
                <Share2 className="h-3 w-3 mr-1" />
                Share
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2">
                <Car className="h-4 w-4 text-primary" />
                <div>
                  <p className="text-lg font-bold">{routeInfo.distanceMiles} mi</p>
                  <p className="text-xs text-muted-foreground">{routeInfo.distance} km</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-primary" />
                <div>
                  <p className="text-lg font-bold">{formatDuration(routeInfo.duration)}</p>
                  <p className="text-xs text-muted-foreground">drive time</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Available Destinations */}
      <div className="flex-1 overflow-y-auto p-4">
        <h3 className="text-sm font-medium mb-3">Available Destinations ({filteredEntities.length})</h3>
        <div className="space-y-2">
          {filteredEntities.map((entity) => {
            const isSelected = selectedStops.some((s) => s.entity_id === entity.entity_id);
            return (
              <button
                key={entity.entity_id}
                onClick={() => isSelected ? removeStop(entity.entity_id) : addStop(entity)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                  isSelected
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50 hover:bg-muted/50'
                }`}
                data-testid={`destination-${entity.entity_id}`}
              >
                <img
                  src={entity.image_url || 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=100'}
                  alt={entity.name}
                  className="w-12 h-12 rounded-lg object-cover"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{entity.name}</p>
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {formatShortLocation(entity)}
                  </p>
                </div>
                {isSelected ? (
                  <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center">
                    <span className="text-xs font-bold">
                      {selectedStops.findIndex((s) => s.entity_id === entity.entity_id) + 1}
                    </span>
                  </div>
                ) : (
                  <Plus className="h-5 w-5 text-muted-foreground" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
};

const RouteSidebar = (props) => {
  const { showSidebar } = props;
  return (
    <div className={`hidden md:flex ${showSidebar ? 'md:w-96' : 'md:w-0'} transition-all duration-300 overflow-hidden md:border-r border-border bg-card flex-col shrink-0`}>
      <RouteSidebarBody {...props} />
    </div>
  );
};

export default RouteSidebar;
